import { type FC, type ReactNode } from "react";

import { useQuery } from "@apollo/client";

import { AssistantChatContainer } from "../../assistant-chat";
import { useFeatureGates } from "../../cof-feature-gates/hooks";
import { OrganizationsStateInitializer } from "../../core/recoil/OrganizationsStateInitializer";
import { OrganizationsV2StateInitializer } from "../../core/recoil/OrganizationsV2StateInitializer";
import { CofConfigId, TosKey } from "../../gql-generated/types";
import { UserInfoContext } from "../../shared/api/contexts";
import {
    type GetUserInfoQuery,
    type GetUserInfoQueryVariables,
    type UserProfileQuery,
    type UserProfileQueryVariables,
    getUserInfo,
    getUserProfile,
} from "../../shared/api/queries";
import { FeedbackRatingContainer } from "../../shared/components";

import { GlobalTosModal } from "./GlobalTosModal";
import { Maintenance } from "./Maintenance";
import { PageContentSpinner } from "./PageContentSpinner";

export const AppContainer: FC<{ children?: ReactNode }> = ({ children }) => {
    const { configs } = useFeatureGates();
    const isMyLensesOrgsV2Enabled = configs[CofConfigId.MyLensesOrgsV2].enabled;

    // V1: fetch getUserInfo (includes orgs, roles, and profile)
    const getUserInfoResultV1 = useQuery<GetUserInfoQuery, GetUserInfoQueryVariables>(getUserInfo, {
        skip: isMyLensesOrgsV2Enabled,
    });

    // V2: only fetch getUserProfile (orgs and roles handled separately)
    const getUserInfoResultV2 = useQuery<UserProfileQuery, UserProfileQueryVariables>(getUserProfile, {
        skip: !isMyLensesOrgsV2Enabled,
    });

    const getUserInfoResult = isMyLensesOrgsV2Enabled ? getUserInfoResultV2 : getUserInfoResultV1;

    if (configs[CofConfigId.MyLensesMaintenanceMode].enabled) {
        return <Maintenance />;
    } else if (getUserInfoResult.error) {
        throw getUserInfoResult.error;
    } else if (getUserInfoResult.data) {
        return (
            <UserInfoContext.Provider value={getUserInfoResult}>
                {isMyLensesOrgsV2Enabled ? <OrganizationsV2StateInitializer /> : <OrganizationsStateInitializer />}
                <div>
                    {children}
                    <FeedbackRatingContainer timeTillVisibleInSeconds={30} />
                    <GlobalTosModal tosKey={TosKey.LensCreatorPayoutTos} />
                    <AssistantChatContainer />
                </div>
            </UserInfoContext.Provider>
        );
    }

    return <PageContentSpinner />;
};
